"use client";

import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldArray, useFormikContext } from "formik";

import ValidationError from "@client/components/debt-payoff-calculator/components/validation-error";
import Currency from "@client/components/formik/currency";
import Field from "@client/components/formik/field";
import Percentage from "@client/components/formik/percentage";
import Select from "@client/components/formik/select";

import { FormButton } from "@ui/js/components/buttons/form-button";
import { SimpleButton } from "@ui/js/components/buttons/simple-button";
import Fieldset from "@ui/js/components/form/fieldset";

import type { debtCardType } from "@packages/types/yup/debt-payoff-calculator";

const DEBT_AMOUNT_MAX = 999999;
const MONTHLY_PAYMENT_MAX = 9999;

export default function DebtPayoffCalculatorForm() {
  // Set approval type
  const { setFieldValue, values } = useFormikContext<any>();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>, i: number) => {
    setFieldValue(`debts.${i}.debtType`, e.currentTarget.value);
  };

  return (
    <div className="grid gap-8 my-8 mx-auto w-full max-w-400">
      <div className="grid gap-2">
        <input type="hidden" name="cardsCreated" value="1" />
        <FieldArray name="debts">
          {({ remove, push }) => {
            return (
              <>
                {values.debts.map((debt: debtCardType, i: number) => {
                  return (
                    <div
                      key={`${i}`}
                      className="rounded-lg shadow-md mb-8 p-4 grid gap-4"
                    >
                      <Fieldset
                        legend={
                          <>
                            <span className="form__legend__text font-semibold">
                              Debt {i + 1}
                            </span>
                          </>
                        }
                      >
                        <Field label="Debt Type" name={`debts.${i}.debtType`}>
                          <Select
                            name={`debts.${i}.debtType`}
                            onChange={(
                              e: React.ChangeEvent<HTMLSelectElement>,
                            ) => handleChange(e, i)}
                          >
                            <option value="" disabled>
                              -- Select an option --
                            </option>
                            <option value="credit_card">Credit Card</option>
                            <option value="personal_loan">Personal Loan</option>
                            <option value="student_loan">Student Loan</option>
                            <option value="other">Other</option>
                          </Select>
                        </Field>
                        <Field label="APR" name={`debts.${i}.interestRate`}>
                          <Percentage name={`debts.${i}.interestRate`} />
                        </Field>
                        <Field label="Total Owed" name={`debts.${i}.amount`}>
                          <Currency
                            id={`debts.${i}.amount`}
                            name={`debts.${i}.amount`}
                            max={DEBT_AMOUNT_MAX}
                          />
                        </Field>
                        <Field
                          label="Monthly Payment"
                          name={`debts.${i}.monthlyPayment`}
                        >
                          <Currency
                            name={`debts.${i}.monthlyPayment`}
                            id={`debts.${i}.monthlyPayment`}
                            max={MONTHLY_PAYMENT_MAX}
                          />
                        </Field>
                        {i + 1 > 1 && (
                          <button
                            type="button"
                            className="justify-self-center text-primary text-sm underline w-fit"
                            onClick={() => remove(i)}
                          >
                            Remove Debt
                          </button>
                        )}
                      </Fieldset>
                    </div>
                  );
                })}
                <SimpleButton
                  id="add-debt-button"
                  text="Add another debt"
                  variant="outlined"
                  iconRight={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => {
                    push({
                      debtType: "",
                      interestRate: "",
                      amount: "",
                      monthlyPayment: "",
                    });
                  }}
                />
                <FormButton text="Calculate" />
                <ValidationError />
              </>
            );
          }}
        </FieldArray>
      </div>
    </div>
  );
}
